<template>
    <div>
        <q-dialog :model-value="show" @hide="cancel">
            <q-card class="q-pa-md bulk-update-dialog">
                <q-form @submit="submit">
                    <div class="text-h6 q-mb-sm">
                        {{ $t('Update selected phases') }}
                    </div>
                    <div class="row q-col-gutter-xs">
                        <div class="col-12 col-sm-1" v-if="!hasOngoingPhase">
                            <q-checkbox
                                id="product-checkbox"
                                v-model="phaseForm.isProductDropdown"
                                color="accent"
                            />
                        </div>
                        <div class="col-12 col-sm-11" v-if="!hasOngoingPhase">
                            <table-select
                                id="product-select"
                                v-if="showProductDropdown"
                                v-model="phaseForm.product"
                                @confirmSelection="
                                    phaseForm.product =
                                        productSelectProps.selection
                                "
                                :baseURL="productSelectProps.url"
                                :queryParams="productSelectProps.queryParams"
                                :label="productSelectProps.label"
                                :dialogTitle="productSelectProps.dialogTitle"
                                :searchLabel="productSelectProps.searchLabel"
                                :rules="formRules.product"
                                row-key="id"
                                class="q-mb-sm"
                                :disable="
                                    !phaseForm.isProductDropdown || isLoading
                                "
                            >
                                <template v-slot:item="data">
                                    <product-card
                                        @select="
                                            productSelectProps.selection = {
                                                label: data.props.row.name,
                                                value: data.props.row.product
                                            }
                                        "
                                        :name="data.props.row.name"
                                        :code="data.props.row.code"
                                        :description="
                                            data.props.row.description
                                        "
                                        :unit-measure="
                                            data.props.row.unit_measure
                                        "
                                        :unit-packing="
                                            data.props.row.unit_packing
                                        "
                                        :isSelected="
                                            productSelectProps.selection &&
                                            productSelectProps.selection
                                                .value ===
                                                data.props.row.product
                                        "
                                        :isSelectable="true"
                                    ></product-card>
                                </template>
                            </table-select>
                        </div>
                    </div>
                    <div class="row q-col-gutter-xs">
                        <div class="col-12 col-sm-1" v-if="!hasRunningPhase">
                            <q-checkbox
                                id="cause-checkbox"
                                v-model="phaseForm.isCauseDropdown"
                                color="accent"
                            />
                        </div>
                        <div class="col-12 col-sm-11" v-if="!hasRunningPhase">
                            <cause-table-select
                                id="cause-select"
                                v-if="showCauseDropdown"
                                v-model="phaseForm.cause"
                                @confirmSelection="
                                    phaseForm.cause = causeSelectProps.selection
                                "
                                :disable="
                                    !phaseForm.isCauseDropdown || isLoading
                                "
                                :baseURL="causeSelectProps.url"
                                :queryParams="causeSelectProps.queryParams"
                                :label="causeSelectProps.label"
                                :dialogTitle="causeSelectProps.dialogTitle"
                                :searchLabel="causeSelectProps.searchLabel"
                                :facilityId="causeSelectProps.facilityId"
                                :rules="formRules.cause"
                                row-key="id"
                                class="q-mb-sm"
                            >
                                <template v-slot:item="data">
                                    <cause-card
                                        @select="
                                            causeSelectProps.selection = {
                                                label: data.props.row.name,
                                                value: data.props.row.id
                                            }
                                        "
                                        :name="data.props.row.name"
                                        :lossType="data.props.row.type_of_loss"
                                        :categoryName="
                                            data.props.row.category_name
                                        "
                                        :planned="data.props.row.planned"
                                        :description="
                                            data.props.row.description
                                        "
                                        :targetDowntime="
                                            data.props.row.target_down_time
                                        "
                                        :isSelected="
                                            causeSelectProps.selection &&
                                            causeSelectProps.selection.value ===
                                                data.props.row.id
                                        "
                                        :isSelectable="true"
                                        :equipmentFailure="
                                            data.props.row.equipment_failure
                                        "
                                    ></cause-card>
                                </template>
                            </cause-table-select>
                        </div>
                    </div>
                    <div class="row q-col-gutter-xs">
                        <div class="col-12 col-sm-1">
                            <q-checkbox
                                id="description-checkbox"
                                v-model="phaseForm.isDescription"
                                color="accent"
                            />
                        </div>
                        <div class="col-12 col-sm-11">
                            <q-input
                                data-test="description-textarea"
                                v-model="phaseForm.description"
                                :disable="!phaseForm.isDescription || isLoading"
                                :label="$t('Description')"
                                :rules="formRules.description"
                                :input-style="'min-height: 45px; max-height: 90px;'"
                                type="textarea"
                                class="q-mb-lg"
                                outlined
                                dense
                                color="accent"
                            ></q-input>
                        </div>
                    </div>
                    <banner
                        class="q-mb-md"
                        :is-visible="hasOngoingPhase"
                        type="info"
                        :message="$t(editPhaseInfoMessage)"
                        :is-dense="$q.screen.lt.sm"
                    />
                    <div class="form__actions">
                        <q-btn
                            id="submit-button"
                            :loading="isLoading"
                            :label="$t('Submit')"
                            :disable="
                                !phaseForm.isDescription &&
                                !phaseForm.isProductDropdown &&
                                !phaseForm.isCauseDropdown
                            "
                            class="float-right q-mb-sm"
                            :class="$q.screen.lt.sm ? 'full-width' : ''"
                            color="primary"
                            type="submit"
                        />
                        <q-btn
                            @click="cancel"
                            :disable="isLoading"
                            :label="$t('Cancel')"
                            class="float-right q-mb-sm q-mr-sm"
                            :class="$q.screen.lt.sm ? 'full-width' : ''"
                            text-color="primary"
                            color="white"
                            outline
                            v-close-popup
                        ></q-btn>
                    </div>
                </q-form>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import Banner from '@components/Banner.vue';
import CauseCard from '@components/CauseCard.vue';
import CauseTableSelect from '@components/CauseTableSelect.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import {
    ASSET_STATUS_TO_STYLE,
    getStatusOptionProperty
} from '@models/assets.js';
import { editPhaseInfoMessage } from '@utils/getMessages.js';

export default {
    name: 'BulkUpdatePhaseDialog',
    components: {
        Banner,
        CauseCard,
        CauseTableSelect,
        ProductCard,
        TableSelect
    },
    emits: ['bulkupdate', 'close'],
    props: {
        assetId: {
            type: [Number, String],
            required: false
        },
        facilityId: {
            type: [Number, String],
            required: false
        },
        phases: {
            type: [Array],
            required: true
        },
        show: {
            type: Boolean,
            required: true
        }
    },
    created() {
        // initialize options
        this.phaseForm.status = this.statusOptions;
    },
    data() {
        return {
            ASSET_STATUS_TO_STYLE,
            phaseForm: {
                cause: null,
                product: null,
                status: null,
                description: '',
                isProductDropdown: false,
                isCauseDropdown: false,
                isDescription: false,
                errorProductText: '',
                errorCauseText: ''
            },
            formRules: {
                product: [
                    val =>
                        this.validateProduct(val) ||
                        this.$t(this.phaseForm.errorProductText)
                ],
                cause: [
                    val =>
                        this.validateCause(val) ||
                        this.$t(this.phaseForm.errorCauseText)
                ],
                status: [val => val || this.$t('Select a status')]
            },
            // selectors
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null,
                facilityId: this.facilityId
            },
            productSelectProps: {
                url: `/assets/${this.assetId}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            isLoading: false,
            editPhaseInfoMessage
        };
    },
    computed: {
        /**
         * Payload to send to server.
         * @return {Object}
         */
        payload() {
            const cause = this.phaseForm.cause
                ? this.phaseForm.cause.value
                : null;
            const product = this.phaseForm.product
                ? this.phaseForm.product.value
                : null;
            const description = this.phaseForm.description;
            const payload = {
                phases: this.phases.map(phase => phase.id)
            };

            if (this.phaseForm.isCauseDropdown) {
                Object.assign(payload, { cause: cause });
            }

            if (this.phaseForm.isProductDropdown) {
                Object.assign(payload, { product: product });
            }

            if (this.phaseForm.isDescription) {
                Object.assign(payload, {
                    description: description
                });
            }

            return payload;
        },
        /**
         * Boolean to check if one of the phases is still ongoing.
         * @return {Boolean}
         */
        hasOngoingPhase() {
            return this.phases.some(phase => phase.end === null);
        },
        /**
         * Check if one of the phases is a running phase.
         * @return {Boolean} - true if one of the phases is running
         */
        hasRunningPhase() {
            return this.phases.some(phase => phase.phase === 'running');
        },
        /**
         * Condition to show cause dropdown.
         */
        showCauseDropdown() {
            if (this.phaseForm.status === null) {
                return false;
            }
            return true;
        },
        /**
         * Condition to show product dropdown.
         */
        showProductDropdown() {
            if (this.phaseForm.status === null) {
                return false;
            }
            return true;
        },
        /**
         * Selectable status options.
         * @return {Object[]}
         */
        statusOptions() {
            // get distinct statuses from the phases to be updated
            const statuses = new Set(this.phases.map(phase => phase.phase));

            // build the selectable phases in q-select format
            const statusOptions = [];
            for (const status of statuses) {
                statusOptions.push({
                    color: getStatusOptionProperty(status, 'color'),
                    label: getStatusOptionProperty(status, 'label'),
                    value: status
                });
            }

            return statusOptions;
        }
    },
    methods: {
        /**
         * Process the bulk update request.
         */
        async submit() {
            this.isLoading = true;

            await this.$axios
                .post('assetphases/bulk_update/', this.payload)
                .then(response => {
                    this.$notifySuccess(this.$t('Phases updated.'));
                    this.$emit('bulkupdate');
                    this.$emit('close');
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    try {
                        if (
                            'phases' in error &&
                            error['phases'][0].includes('Invalid pk')
                        ) {
                            // it's possible that another users may be merging from an outdated phase list, so some phases no longer actually exist in the database
                            this.$notifyError(
                                this.$t(
                                    'Failed to update. One of the phases no longer exists. Please refresh the page and try again.'
                                )
                            );
                        } else {
                            // get the name of the field containing errors
                            const errorKey = Object.keys(error)[0];
                            if (errorKey) {
                                // show backend API message
                                this.$notifyError(error[errorKey]);
                            }
                        }
                    } catch {
                        // show standard error message
                        this.$notifyError(
                            this.$t(
                                'An unexpected error occurred - Failed to update phases. Please try again later.'
                            ),
                            error
                        );
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        /**
         * Close the dialog.
         */
        cancel() {
            this.$emit('close');
        },
        /**
         * Validate cause field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateCause(cause) {
            // check whether cause field needs to be validated
            this.phaseForm.errorCauseText =
                'Cause cannot be updated into phase ';
            for (let status of this.phaseForm.status) {
                if (
                    status['value'] === 'stopped' ||
                    status['value'] === 'running'
                ) {
                    this.phaseForm.errorCauseText += status['value'];
                    return false;
                }
            }
            return true;
        },
        /**
         * Validate product field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateProduct(product) {
            // check whether cause field needs to be validated
            this.phaseForm.errorProductText =
                'Product cannot be updated into phase ';
            for (let status of this.phaseForm.status) {
                if (status['value'] === 'stopped') {
                    this.phaseForm.errorProductText += status['value'];
                    return false;
                }
            }
            return true;
        }
    }
};
</script>

<style lang="scss" scoped>
.bulk-update-dialog {
    width: 90%;
}
</style>
